/**
 * useAppFreshness Hook
 *
 * GW-2028: Combined polling hook that handles:
 * 1. Version detection - fetches /index.html and compares build SHA
 * 2. Feature flag refresh - batch-refetches all known flags
 *
 * Both run on a shared 60-second interval.
 * Polling pauses when the tab is hidden and prevents overlapping invocations.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { checkForNewVersion } from '@/lib/versionCheck.util';
import { useFeatureFlagsStore } from '@/stores/store';

const POLL_INTERVAL_MS = 60_000;

interface AppFreshnessState {
  newVersionSha: string | null;
  showBanner: boolean;
  dismissBanner: () => void;
}

export function useAppFreshness(): AppFreshnessState {
  const { fetchAllKnownFlags } = useFeatureFlagsStore();
  const [newVersionSha, setNewVersionSha] = useState<string | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const dismissedShaRef = useRef<string | null>(null);
  const pollingRef = useRef(false);

  const poll = useCallback(async () => {
    // Version check
    const detectedSha = await checkForNewVersion();
    if (detectedSha) {
      setNewVersionSha(detectedSha);
      if (detectedSha !== dismissedShaRef.current) {
        setShowBanner(true);
      }
    }

    // Feature flag refresh
    await fetchAllKnownFlags();
  }, [fetchAllKnownFlags]);

  const dismissBanner = useCallback(() => {
    dismissedShaRef.current = newVersionSha;
    setShowBanner(false);
  }, [newVersionSha]);

  useEffect(() => {
    const intervalId = setInterval(async () => {
      if (document.visibilityState === 'hidden' || pollingRef.current) return;
      pollingRef.current = true;
      try {
        await poll();
      } finally {
        pollingRef.current = false;
      }
    }, POLL_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [poll]);

  return { newVersionSha, showBanner, dismissBanner };
}
